import { documentId, orderBy } from "firebase/firestore";
import moment from "moment";
import queryString from "query-string";
import React from "react";
import { Alert, Button, Card, ListGroup, Spinner } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router";

import * as api from "../../api/api.jsx";
import {
    useDroneConfigCollectionGroup,
    useOrganizationCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import LoadMoreButton from "../../components/LoadMoreButton/index.jsx";
import MissionFilters from "../../components/MissionFilters/index.jsx";
import MissionListItem from "../../components/MissionListItem/index.jsx";

function AdminAppErrorList({ missions }) {
    const [organizations] = useOrganizationCollection(
        [orderBy("name", "asc")],
        { type: "map" },
    );

    if (missions.length <= 0) {
        return (
            <Alert variant="primary" className="mt-0 m-4">
                No missions found
            </Alert>
        );
    }

    return (
        <>
            {missions?.length > 0 && (
                <ListGroup variant="flush">
                    {missions.map((mission) => (
                        <div
                            className="d-flex align-items-center"
                            key={mission.id}
                        >
                            <Button
                                type="button"
                                onClick={() =>
                                    api.removeAppError(
                                        mission.organizationId,
                                        mission.locationId,
                                        mission.id,
                                    )
                                }
                                className="d-flex justify-content-center align-items-center ms-2 p-1 bg-transparent text-primary"
                                style={{ textDecoration: "none" }}
                            >
                                <FaCheck />
                            </Button>
                            <MissionListItem
                                key={mission.id}
                                mission={mission}
                                timeDisplayFormat="HH:mm:ss"
                                showOrganizationName={true}
                                organizations={organizations}
                                className="ps-0 ms-3"
                            />
                        </div>
                    ))}
                </ListGroup>
            )}
        </>
    );
}

function MissionByDateList({ missions }) {
    const missionsByDate = missions.reduce((acc, mission) => {
        const date = moment(mission.timestamp.toDate()).format("YYYY-MM-DD");
        if (!acc[date]) acc[date] = [];
        acc[date].push(mission);
        return acc;
    }, {});

    return (
        <>
            {Object.keys(missionsByDate)
                .sort()
                .reverse()
                .map((date) => (
                    <Card key={date} className="mb-4">
                        {/* Flex space between */}
                        <Card.Header className="px-4 d-flex justify-content-between align-items-start">
                            <div>
                                <b>{date}</b>
                            </div>
                            <div className="text-muted">
                                <small>
                                    {missionsByDate[date].length} missions
                                </small>
                            </div>
                        </Card.Header>
                        <AdminAppErrorList missions={missionsByDate[date]} />
                    </Card>
                ))}
        </>
    );
}

export default function AdminAppErrorListPage() {
    const location = useLocation();
    const queryParams = queryString.parse(location.search);
    const [filters, setFilters] = React.useState({
        showCorvus: queryParams.showCorvus === "true",
        showDeleted: queryParams.showDeleted === "true",
        droneIds: queryParams.droneIds
            ? queryParams.droneIds.split(",").map((id) => id.toUpperCase())
            : [],
        startDate: queryParams.startDate
            ? new Date(Number(queryParams.startDate))
            : undefined,
        endDate: queryParams.endDate
            ? new Date(Number(queryParams.endDate))
            : undefined,
    });
    const navigate = useNavigate();
    const [limit, setLimit] = React.useState(50);
    const [droneConfigMap] = useDroneConfigCollectionGroup(
        [orderBy(documentId(), "asc")],
        { mode: "once", type: "map" },
    );

    const allDroneIds = Object.keys(droneConfigMap).filter(
        (id) => id !== "presets",
    );
    const [missions, isLoading] = api.useAppErrorMissions(
        limit,
        filters.showCorvus,
        filters.showDeleted,
        filters.droneIds,
        filters.startDate,
        filters.endDate,
    );

    React.useEffect(() => {
        const newQuery = {
            showCorvus: filters.showCorvus ? "true" : undefined,
            showDeleted: filters.showDeleted ? "true" : undefined,
            droneIds:
                filters.droneIds.length > 0
                    ? filters.droneIds.join(",")
                    : undefined,
            startDate: filters.startDate
                ? filters.startDate.getTime()
                : undefined,
            endDate: filters.endDate ? filters.endDate.getTime() : undefined,
        };

        // Remove undefined keys to clean up the URL
        const cleanedQuery = Object.fromEntries(
            Object.entries(newQuery).filter(([, v]) => v !== undefined),
        );

        const newSearch = queryString.stringify(cleanedQuery);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });
    }, [filters, location.pathname, navigate]);

    return (
        <Layout>
            <title>App Errors | MyCorvus</title>
            <Card>
                <Title title="App Errors"></Title>
            </Card>
            <MissionFilters
                className={"my-2"}
                filters={filters}
                setFilters={setFilters}
                droneOptions={allDroneIds}
            />
            {isLoading && (
                <div className="mb-2 d-flex align-items-center text-muted">
                    <Spinner
                        animation="border"
                        role="status"
                        size="sm"
                        variant="primary"
                        className="mx-2"
                    />
                    Loading missions...
                </div>
            )}
            <MissionByDateList missions={missions} />
            <LoadMoreButton
                disabled={limit > missions.length}
                isLoading={isLoading}
                load={() => {
                    setLimit(limit + 100);
                }}
                showLoadAll={false}
                loadAll={() => {}}
                text={`Showing ${missions.length} missions`}
            />
        </Layout>
    );
}
